import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminSkillsAPI, adminCategoriesAPI } from '../services/api';
import SidebarGroup from './SidebarGroup';

const MOBILE_BREAKPOINT = 960;

const Sidebar: React.FC<{ collapsed: boolean; onToggle: () => void; mobileOpen?: boolean; onCloseMobile?: () => void }> = ({
  collapsed,
  onToggle,
  mobileOpen = false,
  onCloseMobile
}) => {
  const location = useLocation();
  const { isAuthenticated, isRecruiter, isAdmin, isCandidate, isJobManagementAdmin, isBillingAdmin, isPartner, isSuperAdmin, isOrgAdmin } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const [skillsPendingCount, setSkillsPendingCount] = React.useState(0);
  React.useEffect(() => {
    if (!isJobManagementAdmin) return;
    const load = async () => {
      try {
        const { count } = await adminSkillsAPI.pendingCount();
        setSkillsPendingCount(count);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [isJobManagementAdmin]);

  const [categoriesPendingCount, setCategoriesPendingCount] = React.useState(0);
  React.useEffect(() => {
    if (!isJobManagementAdmin) return;
    const load = async () => {
      try {
        const { count } = await adminCategoriesAPI.pendingCount();
        setCategoriesPendingCount(count);
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [isJobManagementAdmin]);

  const dashboardPath = isJobManagementAdmin
    ? '/admin/jd-dashboard'
    : isBillingAdmin
      ? '/admin/billing'
    : isAdmin
      ? '/admin/dashboard'
    : isRecruiter
      ? '/recruiter/dashboard'
      : isCandidate
        ? '/candidate/dashboard'
        : isPartner
          ? '/partner/dashboard'
        : '/';

  const profilePath = isAdmin
    ? '/admin/profile'
    : isRecruiter
      ? '/recruiter/profile'
      : isPartner
        ? '/partner/profile'
        : '/profile';

  const handleNavClick = () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      onCloseMobile?.();
    }
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar__top">
        <Link to="/" className="sidebar__brand" onClick={handleNavClick}>
          <span className="brand-badge">TJ</span>
          {!collapsed && <span>talixjobs</span>}
        </Link>
        <button className="sidebar__toggle" onClick={onToggle} aria-label="Toggle sidebar">
          <span />
          <span />
          <span />
        </button>
      </div>

      <nav className="sidebar__nav">
        {!isAuthenticated && (
          <Link to="/" className={`sidebar__link ${isActive('/') ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="sidebar__label">Home</span>
          </Link>
        )}
        {!isRecruiter && !isBillingAdmin && !isPartner && (
          <Link to="/jobs" className={`sidebar__link ${isActive('/jobs') ? 'active' : ''}`} onClick={handleNavClick}>
            <span className="sidebar__label">Jobs</span>
          </Link>
        )}

        {isAuthenticated && (
          <>
            {(isCandidate || isRecruiter) && (
              <Link to={dashboardPath} className={`sidebar__link ${location.pathname.includes('dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                <span className="sidebar__label">Dashboard</span>
              </Link>
            )}

            {isCandidate && (
              <>
                <Link to="/applications" className={`sidebar__link ${isActive('/applications') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">My Applications</span>
                </Link>
                <Link to="/saved-jobs" className={`sidebar__link ${isActive('/saved-jobs') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">Saved Jobs</span>
                </Link>
              </>
            )}

            {isRecruiter && (
              <>
                <Link to="/recruiter/talent" className={`sidebar__link ${isActive('/recruiter/talent') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">Talent</span>
                </Link>
                <Link to="/recruiter/jobs" className={`sidebar__link ${isActive('/recruiter/jobs') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">My Jobs</span>
                </Link>
                <Link to="/recruiter/applications" className={`sidebar__link ${isActive('/recruiter/applications') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">Applications</span>
                </Link>
                <Link to="/recruiter/buckets" className={`sidebar__link ${isActive('/recruiter/buckets') ? 'active' : ''}`} onClick={handleNavClick}>
                  <span className="sidebar__label">Buckets</span>
                </Link>
                {isOrgAdmin && (
                  <>
                    <Link to="/recruiter/org-members" className={`sidebar__link ${isActive('/recruiter/org-members') ? 'active' : ''}`} onClick={handleNavClick}>
                      <span className="sidebar__label">Org Members</span>
                    </Link>
                    <Link to="/recruiter/org/jobs" className={`sidebar__link ${isActive('/recruiter/org/jobs') ? 'active' : ''}`} onClick={handleNavClick}>
                      <span className="sidebar__label">All Org Jobs</span>
                    </Link>
                    <Link to="/recruiter/org/invites" className={`sidebar__link ${isActive('/recruiter/org/invites') ? 'active' : ''}`} onClick={handleNavClick}>
                      <span className="sidebar__label">Org Invites</span>
                    </Link>
                  </>
                )}
              </>
            )}

            {isAdmin && !isJobManagementAdmin && !isBillingAdmin && (
              <>
                <SidebarGroup
                  title="Overview"
                  storageKey="sidebar-group:super-admin:overview"
                  activePaths={['/admin/dashboard']}
                  hidden={collapsed}
                >
                  <Link to={dashboardPath} className={`sidebar__link ${location.pathname.includes('dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Dashboard</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Users & Content"
                  storageKey="sidebar-group:super-admin:users-and-content"
                  activePaths={['/admin/users', '/admin/jobs', '/admin/support']}
                  hidden={collapsed}
                >
                  <Link to="/admin/users" className={`sidebar__link ${isActive('/admin/users') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Users</span>
                  </Link>
                  <Link to="/admin/jobs" className={`sidebar__link ${isActive('/admin/jobs') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">All Jobs</span>
                  </Link>
                  <Link to="/admin/support" className={`sidebar__link ${isActive('/admin/support') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Support</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="System"
                  storageKey="sidebar-group:super-admin:system"
                  activePaths={['/admin/settings', '/admin/org-caps', '/admin/activity']}
                  hidden={collapsed}
                >
                  <Link to="/admin/settings" className={`sidebar__link ${isActive('/admin/settings') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Settings</span>
                  </Link>
                  <Link to="/admin/org-caps" className={`sidebar__link ${isActive('/admin/org-caps') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Org Caps</span>
                  </Link>
                  {isSuperAdmin && (
                    <Link to="/admin/settings/org-cap" className={`sidebar__link ${isActive('/admin/settings/org-cap') ? 'active' : ''}`} onClick={handleNavClick}>
                      <span className="sidebar__label">Org Cap Settings</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link to="/admin/activity" className={`sidebar__link ${isActive('/admin/activity') ? 'active' : ''}`} onClick={handleNavClick}>
                      <span className="sidebar__label">Activity</span>
                    </Link>
                  )}
                </SidebarGroup>
              </>
            )}

            {isBillingAdmin && (
              <>
                <SidebarGroup
                  title="Overview"
                  storageKey="sidebar-group:billing-admin:overview"
                  activePaths={['/admin/billing/dashboard']}
                  hidden={collapsed}
                >
                  <Link to="/admin/billing/dashboard" className={`sidebar__link ${isActive('/admin/billing/dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Dashboard</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Billing"
                  storageKey="sidebar-group:billing-admin:billing"
                  activePaths={['/admin/billing/coupons', '/admin/billing/invoices', '/admin/billing/payments', '/admin/billing/payment-mappings', '/admin/billing/pricing-controls', '/admin/billing/recruiter-exceptions']}
                  hidden={collapsed}
                >
                  <Link to="/admin/billing" className={`sidebar__link ${isActive('/admin/billing') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Billing</span>
                  </Link>
                  <Link to="/admin/billing/coupons" className={`sidebar__link ${location.pathname.startsWith('/admin/billing/coupons') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Coupon Management</span>
                  </Link>
                  <Link to="/admin/billing/invoices" className={`sidebar__link ${isActive('/admin/billing/invoices') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Invoices</span>
                  </Link>
                  <Link to="/admin/billing/payments" className={`sidebar__link ${isActive('/admin/billing/payments') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Payments</span>
                  </Link>
                  <Link to="/admin/billing/payment-mappings" className={`sidebar__link ${isActive('/admin/billing/payment-mappings') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Payment Mappings</span>
                  </Link>
                  <Link to="/admin/billing/pricing-controls" className={`sidebar__link ${isActive('/admin/billing/pricing-controls') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Pricing Controls</span>
                  </Link>
                  <Link to="/admin/billing/recruiter-exceptions" className={`sidebar__link ${isActive('/admin/billing/recruiter-exceptions') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Recruiter Exceptions</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Partners"
                  storageKey="sidebar-group:billing-admin:partners"
                  activePaths={['/admin/billing/partners', '/admin/partner-uploads', '/admin/affiliate-referrals']}
                  hidden={collapsed}
                >
                  <Link to="/admin/billing/partners" className={`sidebar__link ${isActive('/admin/billing/partners') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Partners</span>
                  </Link>
                  <Link to="/admin/partner-uploads" className={`sidebar__link ${isActive('/admin/partner-uploads') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Partner Uploads</span>
                  </Link>
                  <Link to="/admin/partner-uploads/approved" className={`sidebar__link ${isActive('/admin/partner-uploads/approved') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Approved Profiles</span>
                  </Link>
                  <Link to="/admin/affiliate-referrals" className={`sidebar__link ${isActive('/admin/affiliate-referrals') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Affiliate Referrals</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Users & Orgs"
                  storageKey="sidebar-group:billing-admin:users-and-orgs"
                  activePaths={['/admin/billing/org-admins', '/admin/kyc-reviews']}
                  hidden={collapsed}
                >
                  <Link to="/admin/billing/org-admins" className={`sidebar__link ${isActive('/admin/billing/org-admins') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Org Admins</span>
                  </Link>
                  <Link to="/admin/kyc-reviews" className={`sidebar__link ${isActive('/admin/kyc-reviews') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">KYC Reviews</span>
                  </Link>
                </SidebarGroup>
              </>
            )}

            {isPartner && (
              <>
                <SidebarGroup
                  title="Overview"
                  storageKey="sidebar-group:partner:overview"
                  activePaths={['/partner/dashboard']}
                  hidden={collapsed}
                >
                  <Link to="/partner/dashboard" className={`sidebar__link ${location.pathname.includes('dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Dashboard</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Work"
                  storageKey="sidebar-group:partner:work"
                  activePaths={['/partner/uploads', '/partner/delegated-jobs']}
                  hidden={collapsed}
                >
                  <Link to="/partner/uploads" className={`sidebar__link ${isActive('/partner/uploads') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Uploads</span>
                  </Link>
                  <Link
                    to="/partner/delegated-jobs"
                    className={`sidebar__link ${location.pathname === '/partner/delegated-jobs' || location.pathname.startsWith('/partner/delegated-jobs/') && !location.pathname.endsWith('/my-submissions') ? 'active' : ''}`}
                    onClick={handleNavClick}
                  >
                    <span className="sidebar__label">Delegated Jobs</span>
                  </Link>
                  <Link to="/partner/delegated-jobs/my-submissions" className={`sidebar__link ${isActive('/partner/delegated-jobs/my-submissions') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">My Submissions</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="Growth"
                  storageKey="sidebar-group:partner:growth"
                  activePaths={['/partner/affiliate-links']}
                  hidden={collapsed}
                >
                  <Link to="/partner/affiliate-links" className={`sidebar__link ${isActive('/partner/affiliate-links') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Affiliate Links</span>
                  </Link>
                </SidebarGroup>
              </>
            )}

            {isJobManagementAdmin && (
              <>
                <SidebarGroup
                  title="Overview"
                  storageKey="sidebar-group:jd-mgmt-admin:overview"
                  activePaths={['/admin/jd-dashboard']}
                  hidden={collapsed}
                >
                  <Link to="/admin/jd-dashboard" className={`sidebar__link ${isActive('/admin/jd-dashboard') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Dashboard</span>
                  </Link>
                </SidebarGroup>
                <SidebarGroup
                  title="JD Ops"
                  storageKey="sidebar-group:jd-mgmt-admin:jd-ops"
                  activePaths={['/admin/jd-review', '/admin/external-jobs', '/admin/skills', '/admin/categories']}
                  hasBadge={skillsPendingCount > 0 || categoriesPendingCount > 0}
                  hidden={collapsed}
                >
                  <Link to="/admin/jd-review" className={`sidebar__link ${isActive('/admin/jd-review') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">JD Review</span>
                  </Link>
                  <Link to="/admin/external-jobs" className={`sidebar__link ${location.pathname.startsWith('/admin/external-jobs') && location.pathname !== '/admin/external-jobs/import' ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">External Jobs</span>
                  </Link>
                  <Link to="/admin/external-jobs/import" className={`sidebar__link ${isActive('/admin/external-jobs/import') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Import CSV</span>
                  </Link>
                  <Link to="/admin/skills" className={`sidebar__link ${isActive('/admin/skills') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Skills</span>
                    {skillsPendingCount > 0 && (
                      <span className="badge badge--warning" style={{ marginLeft: 8 }}>
                        {skillsPendingCount}
                      </span>
                    )}
                  </Link>
                  <Link to="/admin/categories" className={`sidebar__link ${isActive('/admin/categories') ? 'active' : ''}`} onClick={handleNavClick}>
                    <span className="sidebar__label">Categories</span>
                    {categoriesPendingCount > 0 && (
                      <span className="badge badge--warning" style={{ marginLeft: 8 }}>
                        {categoriesPendingCount}
                      </span>
                    )}
                  </Link>
                </SidebarGroup>
              </>
            )}

            <Link to={profilePath} className={`sidebar__link ${isActive(profilePath) ? 'active' : ''}`} onClick={handleNavClick}>
              <span className="sidebar__label">Profile</span>
            </Link>
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
